import React, { Fragment, useEffect, useState } from "react"

import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"

import CheckIcon from "@mui/icons-material/Check"
import RemoveIcon from "@mui/icons-material/Remove"
import { Box } from "@mui/material"
import Grid from "@mui/material/Grid"
import { Typography } from "@synapse-analytics/synapse-ui"
import type { MRT_ColumnDef, MRT_PaginationState } from "material-react-table"
import moment from "moment"

import { getTheme } from "../../hooks/UseTheme"
import { ExportJobCreateRequest } from "../../types/generated/api/ExportJobCreateRequest"
import type { ListModelOutputs } from "../../types/generated/api/ListModelOutputs"
import type { ListPredictions } from "../../types/generated/api/ListPredictions"
import type { ListPredictionsOutput } from "../../types/generated/api/ListPredictionsOutput"
import type { Model } from "../../types/generated/api/Model"
import type { ModelListCreate } from "../../types/generated/api/ModelListCreate"
import { TableConfigurationRequest } from "../../types/generated/api/TableConfigurationRequest"
import { KonanJsonView } from "../data-display/KonanJsonView"
import { ExportDataDialog } from "../dialogs/ExportDataDialog"
import { BaseTableContainer } from "./BaseTableContainer"

type Props = {
  modelId?: string
  data: Array<ListModelOutputs> | ListPredictions
  rowCount: number | undefined
  isFetching: boolean
  pagination: MRT_PaginationState
  setPagination: React.Dispatch<React.SetStateAction<MRT_PaginationState>>
  modelName: string | ((currentModel: string) => Model | ModelListCreate | null)
}

/**
 * RequestLogTable component displays a table of request logs.
 * @param {Props} Props
 * @param {string} [modelId] - The Id of the model.
 * @param {Array<ListModelOutputs> | ListPredictions} data - The data to display in the table.
 * @param {number | undefined} rowCount - The total number of rows for pagination.
 * @param {boolean} isFetching - Indicates if data is being fetched.
 * @param {MRT_PaginationState} pagination - The current pagination state.
 * @param {function} setPagination - Function to update pagination state.
 * @param {string | function} modelName - model name or function to get model by Id.
 * @returns {React.ReactElement}
 */
export function RequestLogTable(props: Readonly<Props>): React.ReactElement {
  const { t } = useTranslation("tables")
  const { modelId, data, rowCount, isFetching, pagination, setPagination, modelName } = props
  const [searchParams] = useSearchParams()

  const theme = getTheme()
  // start and end date state for fetching api request data
  const startDate = searchParams.get("startDate")
  const endDate = searchParams.get("endDate")
  const [isExportDialogOpen, setIsExportDialogOpen] = useState<boolean>(false)

  // Define columns for the table
  const columns: MRT_ColumnDef<ListPredictionsOutput>[] = [
    {
      header: t("requestLog.columns.requestDate"),
      accessorKey: "request_date",
      accessorFn: (row) => moment(row.created_at).utc().format("DD/MM/YYYY"),
    },
    {
      header: t("requestLog.columns.requestTime"),
      accessorKey: "created_at_utc",
      accessorFn: (row) => moment(row.created_at).utc().format("hh:mm:ss A"),
    },
    {
      header: t("requestLog.columns.modelUsed"),
      accessorKey: "model_name",
      accessorFn: (row) => {
        // if ListModelOutputs, try to get model id to get model name
        const modelId = (row as unknown as ListModelOutputs)?.model

        return (
          row.model_name ??
          (typeof modelName === "string" ? modelName : (modelName(modelId)?.name ?? t("requestLog.notAvailable")))
        )
      },
    },
    {
      header: t("requestLog.columns.konanStatusCode"),
      accessorKey: "be_status_code",
      // Casting to enable search
      accessorFn: (row) => row.be_status_code?.toString(),
      Cell: ({ row }) => {
        if (row.original.be_status_code) {
          return (
            <span
              style={{
                color: row.original.be_status_code >= 400 ? theme.palette.red.text[2] : theme.palette.green.text[2],
              }}
            >
              {row.original.be_status_code}
            </span>
          )
        } else {
          return <span>{row.original.be_status_code}</span>
        }
      },
    },
    {
      header: t("requestLog.columns.modelStatusCode"),
      accessorKey: "mls_status_code",
      // Casting to enable search
      accessorFn: (row) => row.mls_status_code?.toString(),
      Cell: ({ row }) => {
        if (row.original.mls_status_code) {
          return (
            <span
              style={{
                color: row.original.mls_status_code >= 400 ? theme.palette.red.text[2] : theme.palette.green.text[2],
              }}
            >
              {row.original.mls_status_code}
            </span>
          )
        } else {
          return <span style={{ color: theme.palette.yellow.text[2] }}>NA</span>
        }
      },
    },
    {
      header: t("requestLog.columns.requestedBy"),
      accessorKey: "user",
    },
    {
      header: t("requestLog.columns.konanResponseTime"),
      accessorKey: "be_response_time",
      accessorFn: (row) => {
        if (row.be_response_time) return row.be_response_time + " ms"
        else {
          return <span style={{ color: theme.palette.yellow.text[2] }}>NA</span>
        }
      },
    },
    {
      header: t("requestLog.columns.modelResponseTime"),
      accessorKey: "mls_response_time",
      accessorFn: (row) => {
        if (row.mls_response_time) return row.mls_response_time + " ms"
        else {
          return <span style={{ color: theme.palette.yellow.text[2] }}>NA</span>
        }
      },
    },
    {
      header: t("requestLog.columns.feedback"),
      accessorKey: "feedback",
      Cell: ({ row }) => {
        return row.original.feedback ? <CheckIcon /> : <RemoveIcon />
      },
      enableColumnFilter: false,
    },
  ]

  // Function to format the response JSON body
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const responseJsonBody = (rowData: Record<string, any> | null, uuid: string): Record<string, any> => {
    const response = {
      uuid: uuid,
      ...rowData,
    }
    return response
  }

  // Reset pagination when date range changes
  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate])

  return (
    <Fragment>
      {isExportDialogOpen && (
        <ExportDataDialog
          onClose={() => setIsExportDialogOpen(false)}
          jobType={
            modelId ? ExportJobCreateRequest.type.MODEL_PREDICTIONS : ExportJobCreateRequest.type.DEPLOYMENT_PREDICTIONS
          }
          model={modelId}
        />
      )}

      <BaseTableContainer
        title={t("requestLog.title")}
        columns={columns as MRT_ColumnDef[]}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data={(modelId ? (data as any[]) : ((data as ListPredictions)?.outputs as any[])) ?? []}
        isLoading={isFetching}
        manualPagination
        pagination={pagination}
        source={TableConfigurationRequest.table.API_REQUESTS}
        setPagination={setPagination}
        rowCount={rowCount}
        onExportClick={() => setIsExportDialogOpen(true)}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        detailPanel={(row: any) => {
          const rowData = row.original as ListPredictionsOutput
          return (
            <Box p={2}>
              <Grid container spacing={2}>
                <Grid
                  item
                  xs={
                    rowData.feedback && rowData.workflow_output
                      ? 3
                      : rowData.feedback || rowData.workflow_output
                        ? 4
                        : 6
                  }
                >
                  <Typography variant="p" gutterBottom variantColor={2}>
                    Request Body
                  </Typography>
                  <KonanJsonView src={rowData.features} />
                </Grid>

                <Grid
                  item
                  xs={
                    rowData.feedback && rowData.workflow_output
                      ? 3
                      : rowData.feedback || rowData.workflow_output
                        ? 4
                        : 6
                  }
                >
                  <Typography variant="p" gutterBottom variantColor={2}>
                    Response
                  </Typography>
                  <KonanJsonView src={responseJsonBody(rowData.mls_output_json, rowData.uuid) ?? rowData.mls_output} />
                </Grid>

                {rowData.feedback && (
                  <Grid item xs={rowData.workflow_output ? 3 : 4}>
                    <Typography variant="p" gutterBottom variantColor={2}>
                      Feedback
                    </Typography>
                    <KonanJsonView src={rowData.feedback} />
                  </Grid>
                )}

                {rowData.workflow_output && (
                  <Grid item xs={rowData.feedback ? 3 : 4}>
                    <Typography variant="p" gutterBottom variantColor={2}>
                      Workflow Output
                    </Typography>
                    <KonanJsonView src={rowData.workflow_output} />
                  </Grid>
                )}
              </Grid>
            </Box>
          )
        }}
      />
    </Fragment>
  )
}
